import axios, { AxiosRequestConfig } from 'axios'
import queryString from 'query-string'
import type { TransactionsFiltersBody } from 'src/components/TransactionsListFilter/transactionsFilter.schema'
import { PaginationResponse } from 'src/models/pagination.models'
import {
  TransactionAiInsightFeedbackType,
  type TransactionAiInsights,
  TransactionDetailResponse,
  TransactionListResponse,
} from 'src/models/transaction.models'
import { getResponseData } from 'src/queries/network.utils'
import type {
  ExpenseCategoryLabelFiltersBody,
  ExpenseCategoryLabelResponse,
} from 'src/queries/organizations/expenseCategories.queries'
import { getRequestConfig, RequestConfig } from 'src/queries/requestConfig'

export interface TransactionsResponse {
  report: {
    page: number
    perPage: number
    count: number
    transactions: TransactionListResponse[]
  }
}

interface TransactionExpenseCategoryLabelDetails {
  rank: number
  similarityScore: number
  similarTransactionId?: string
}

export interface TransactionExpenseCategoryLabelResponse
  extends ExpenseCategoryLabelResponse {
  suggestionDetails?: TransactionExpenseCategoryLabelDetails
}

export interface TransactionExpenseCategoryLabelsResponse {
  pagination: PaginationResponse
  expenseLabels: TransactionExpenseCategoryLabelResponse[]
}

export const getTransactions = async ({
  filters,
  page,
  perPage,
  myTransactions,
  currentUserId,
}: {
  filters: TransactionsFiltersBody | undefined
  page: number
  perPage: number
  myTransactions: boolean
  currentUserId: string | undefined
}): Promise<TransactionsResponse> => {
  const requestUrl = myTransactions
    ? `/reports/transactions/v2/${currentUserId}`
    : '/reports/transactions/v2'
  return getResponseData(
    axios.get<TransactionsResponse>(
      `${requestUrl}?${queryString.stringify({ ...filters, page, perPage })}`,
      getRequestConfig(RequestConfig.Default),
    ),
  )
}

export const getTransactionDetail = async (
  transactionId: string,
): Promise<TransactionDetailResponse> => {
  return getResponseData(
    axios.get<TransactionDetailResponse>(
      `/transactions/${transactionId}`,
      getRequestConfig(RequestConfig.Default),
    ),
  )
}

export const getTransactionsCount = async (
  filters: TransactionsFiltersBody | undefined,
): Promise<{ count: number }> => {
  return getResponseData(
    axios.get<{ count: number }>(
      `/reports/transactions/count?${queryString.stringify({ ...filters })}`,
      getRequestConfig(RequestConfig.Default),
    ),
  )
}

export const getTransactionExpenseCategoryLabels = async ({
  transactionId,
  expenseCategoryId,
  filters,
  page,
}: {
  transactionId: string
  expenseCategoryId: string
  filters: ExpenseCategoryLabelFiltersBody
  page: number
}): Promise<TransactionExpenseCategoryLabelsResponse> => {
  return getResponseData(
    axios.get<TransactionExpenseCategoryLabelsResponse>(
      `transactions/${transactionId}/expensedata/categories/${expenseCategoryId}/labels?${queryString.stringify(
        { page, ...filters },
        { skipEmptyString: true },
      )}`,
      getRequestConfig(RequestConfig.Default),
    ),
  )
}

export interface PutTransactionAiInsightFeedbackRequest {
  feedbackType: TransactionAiInsightFeedbackType
}

export const putTransactionAiInsightFeedback = async (
  transactionId: string,
  insightId: string,
  request: PutTransactionAiInsightFeedbackRequest,
  requestConfig: AxiosRequestConfig,
): Promise<TransactionAiInsights> => {
  return getResponseData(
    axios.put<TransactionAiInsights>(
      `/transactions/${transactionId}/ai-insights/${insightId}/feedback`,
      request,
      requestConfig,
    ),
  )
}
